import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { VenueMasterFormPage } from "./VenueMasterFormPage";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Label } from "../../../ui/label";
import {
  Plus,
  Search,
  Edit2,
  Building2,
  Mail,
  Phone,
  Globe,
  X,
  Filter,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import type { VenueFormData } from "./VenueMasterFormPage";
import { getVenuesList, addVenue, updateVenue } from "../../../services/venueService";
import { useScreenPermission } from "../../../hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
interface VenueBlock {
  id: string;
  blockType: "absolute" | "festival";
  fromDate: string;
  toDate: string;
  arrivalBlockTime: string;
  departureBlockTime: string;
  remark: string;
}

interface Venue {
  id: string;
  name: string;
  description: string;
  notification: string;
  contactNo: string;
  email: string;
  cutoffDays: number;
  systemTimelineDays: number;
  postCutoffDays: number;
  timeZone: string;
  isActive: boolean;
  blocks: VenueBlock[];
}

export const VenueMasterEnhanced = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [isVenueDialogOpen, setIsVenueDialogOpen] = useState(false);

  const [timeZones, setTimeZones] = useState<string[]>([]);
  const [, setListLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    venueName: "",
    status: "all" as "all" | "active" | "inactive",
    timeZone: "all",
    hasBlocks: "all" as "all" | "yes" | "no",
  });

  const [venues, setVenues] = useState<Venue[]>([]);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { canAdd, canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
      onBack: undefined,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    setListLoading(true);

    const delay = setTimeout(() => {
      setListLoading(false);
    }, 200);

    return () => clearTimeout(delay);
  }, [searchTerm, filters, pageNo, pageSize]);

  useEffect(() => {
    const loadVenues = async () => {
      try {
        setListLoading(true);
        setLoading(true);

        const response = await getVenuesList({
          searchText: searchTerm || null,
          venueName: filters.venueName || null,
          isActive:
            filters.status === "all" ? null : filters.status === "active",
          timezone: filters.timeZone === "all" ? null : filters.timeZone,
          pageNo,
          pageSize,
        });

        const mapped = response.items.map((v) => ({
          id: String(v.venueId),
          name: v.venueName,
          description: v.description || "",
          notification: "",
          contactNo: v.contactNo,
          email: v.emailAddress,
          cutoffDays: 0,
          systemTimelineDays: 0,
          postCutoffDays: 0,
          timeZone: v.timeZone,
          isActive: v.isActive,
          blocks: [],
        }));

        setVenues(mapped);
        setTotalCount(response.totalCount);

        const uniqueTZ = Array.from(
          new Set(response.items.map((v) => v.timeZone).filter(Boolean))
        );
        setTimeZones(uniqueTZ);
      } catch (err) {
        toast.error("Failed to load venues");
      } finally {
        setListLoading(false);
        setLoading(false);
      }
    };

    loadVenues();
  }, [searchTerm, filters, pageNo, pageSize]);

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm, filters]);



  const mapVenueToFormData = (venue: Venue): VenueFormData => ({
    venueName: venue.name,
    description: venue.description,
    email: venue.email,
    contactNo: venue.contactNo,
    timeZone: venue.timeZone,
    isActive: venue.isActive,
  });

  const filteredVenues = venues;

  const handleClearFilters = () => {
    setFilters({
      venueName: "",
      status: "all",
      timeZone: "all",
      hasBlocks: "all",
    });
    toast.success("Filters cleared");
  };

  const handleOpenForm = (venue?: Venue) => {
    if (venue) {
      setIsEditMode(true);
      setSelectedVenue(venue);
    } else {
      setIsEditMode(false);
      setSelectedVenue(null);
    }

    setIsVenueDialogOpen(true);
  };

  const handleBackToList = useCallback(() => {
    setIsVenueDialogOpen(false);
    setIsEditMode(false);
    setSelectedVenue(null);
  }, []);

  const handleSaveVenueFromPage = async (form: VenueFormData) => {
    try {
      setLoading(true);
      if (isEditMode && selectedVenue) {
        const payload = {
          venueId: Number(selectedVenue.id),
          venueName: form.venueName,
          venueCode: form.description || "",
          notification: selectedVenue.notification || "",
          contactNo: form.contactNo,
          emailAddress: form.email,
          cutOffDays: selectedVenue.cutoffDays || 0,
          systemTimelineDays: selectedVenue.systemTimelineDays || 0,
          postCutOffDays: selectedVenue.postCutoffDays || 0,
          timeZone: form.timeZone,
          isActive: form.isActive,
          updatedBy: 1,
        };

        const res = await updateVenue(payload);

        if (res.updated) {
          toast.success("Venue updated successfully!");
        } else {
          toast.error("Update failed");
        }
      } else {
        const payload = {
          venueName: form.venueName,
          venueCode: form.description || "",
          notification: "",
          contactNo: form.contactNo,
          emailAddress: form.email,
          cutOffDays: 0,
          systemTimelineDays: 0,
          postCutOffDays: 0,
          timeZone: form.timeZone,
          isActive: form.isActive,
          createdBy: 1,
        };

        await addVenue(payload);
        toast.success("Venue added successfully!");
      }
      setTimeout(() => {
        handleBackToList();
      }, 500);

      const response = await getVenuesList({
        searchText: null,
        venueName: null,
        isActive: null,
        timezone: null,
        pageNo,
        pageSize,
      });

      const mapped = response.items.map((v) => ({
        id: String(v.venueId),
        name: v.venueName,
        description: v.description ?? "",
        notification: "",
        contactNo: v.contactNo,
        email: v.emailAddress,
        cutoffDays: 0,
        systemTimelineDays: 0,
        postCutoffDays: 0,
        timeZone: v.timeZone,
        isActive: v.isActive,
        blocks: [],
      }));

      setVenues(mapped);
    } catch (err) {
      console.error("Failed:", err);
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const StatusPill = ({ active }: { active: boolean }) => (
    <span
      className={
        active
          ? "px-3 py-1 rounded-full text-xs font-semibold bg-[#D9F5D9] text-[#2B7F2B]"
          : "px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700"
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Search and Filter Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2" style={{ marginBottom: "10px" }}>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search venues..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            style={showFilters ? { background: "#5B9BD5" } : {}}
            className={`shrink-0 ${
              showFilters
                ? "text-white"
                : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
            }`}
            title="Toggle Advanced Filters"
          >
            <Filter className="w-5 h-5" />
          </Button>
          {canAdd && (
            <Button
              onClick={() => handleOpenForm()}
              style={{ backgroundColor: "#5B9BD5" }}
              className="text-white shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Venue
            </Button>
          )}
        </div>

        {/* Advanced Filters Section */}
        {showFilters && (
          <Card
            className="bg-white border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px", marginBottom: "10px", marginTop: "10px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter venues by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Venue Name Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-venue-name" className="text-sm">
                    Venue Name
                  </Label>
                  <Input
                    id="filter-venue-name"
                    placeholder="Search by venue name..."
                    value={filters.venueName}
                    onChange={(e) =>
                      setFilters({ ...filters, venueName: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value: "all" | "active" | "inactive") =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Zone Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-timezone" className="text-sm">
                    Time Zone
                  </Label>
                  <Select
                    value={filters.timeZone}
                    onValueChange={(value) =>
                      setFilters({ ...filters, timeZone: value })
                    }
                  >
                    <SelectTrigger id="filter-timezone" className="h-9 text-sm">
                      <SelectValue placeholder="All time zones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time Zones</SelectItem>

                      {timeZones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary & Clear Button */}
              {(filters.venueName ||
                filters.status !== "all" ||
                filters.timeZone !== "all" ||
                filters.hasBlocks !== "all") && (
                <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {filters.venueName && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Venue: {filters.venueName}
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                      {filters.timeZone !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          TZ: {filters.timeZone}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                    className="border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Venues Grid */}
        <div className="grid gap-4" style={{ marginTop: "0px", marginBottom: "0px" }}>
          {filteredVenues.map((venue) => (
              <Card
                key={venue.id}
                className="border-[#5B9BD5]/30"
                style={{ gap: "0px", marginTop: "0px", backgroundColor: "#FFFFFF"}}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#5B9BD5] to-purple-600 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle style={{ color: "#5B9BD5" }}>
                          {venue.name}
                        </CardTitle>
                        <CardDescription>{venue.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill active={venue.isActive} />
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenForm(venue)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-600">Email:</span>
                          <span className="text-gray-900">{venue.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-600">Contact:</span>
                          <span className="text-gray-900">{venue.contactNo}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Globe className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-600">Time Zone:</span>
                          <span className="text-gray-900">{venue.timeZone}</span>
                        </div>
                      </div>
                    </div>
                    {venue.notification && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-900">
                          <strong>Notification:</strong> {venue.notification}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          }
        </div>
        <Pagination
          totalItems={totalCount}
          itemsPerPage={pageSize}
          currentPage={pageNo}
          onPageChange={(page) => setPageNo(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageNo(1);
          }}
          label="venues"
        />
      </div>

      <Dialog
        open={isVenueDialogOpen}
        onOpenChange={setIsVenueDialogOpen}
      >
        <DialogContent className="bg-white sm:max-w-[850px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              {isEditMode ? "Edit Venue" : "Add Venue"}
            </DialogTitle>

            <DialogDescription>
              {isEditMode
                ? "Update venue details"
                : "Create a new venue"}
            </DialogDescription>
          </DialogHeader>

          <VenueMasterFormPage
            onBack={handleBackToList}
            editMode={isEditMode}
            initialData={
              selectedVenue ? mapVenueToFormData(selectedVenue) : undefined
            }
            timeZones={timeZones}
            onSaveVenue={handleSaveVenueFromPage}
          />
        </DialogContent>
      </Dialog>
    </>

    
  );
};
